import { parseQueryParamsObject, Route } from "@robe/core";
import { LinkableContent, ContentLinkOptions } from ".";
import { routeSlugPairFromContent } from "./routeSlugPairFromContent";
import { ID_REGEX, ContentLinkContextData, resolveRouting, SLUG_REGEX } from "./utils";

/**
 * Creates url path for content that corresponds to next.js route in `pages` folder.
 *
 * @example
 * - `/product/[id]?id=4887`
 * - `/user/registration/activation`
 */
export const href = (data: ContentLinkContextData) => (content: LinkableContent, options?: ContentLinkOptions) => {
  const { contentType } = data;
  const { contentLocale } = resolveRouting(data, options);
  if (!contentLocale) {
    return null;
  }

  const routeSlugPair = routeSlugPairFromContent(data, { content, contentType, contentLocale });
  if (!routeSlugPair || !routeSlugPair?.route) {
    return null;
  }

  const hrefPath = routeSlugPair.route.toPath.replace("{id}", "[id]").replace("{slug}", "[slug]");

  const queryParams = resolveQueryParams({
    content,
    routeSlugPair: routeSlugPair as RouteSlugPairRouteMandated,
    hrefPath,
    queries: options?.queries,
  });

  const anchor = options?.anchor ? `#${options.anchor}` : "";

  return `${hrefPath}${queryParams}${anchor}`;
};

type RouteSlugPairRouteMandated = {
  slug?: string | null;
  route: Route;
};

interface ResolveQueryParamsProps {
  content: LinkableContent;
  routeSlugPair: RouteSlugPairRouteMandated;
  hrefPath: string;
  queries?: Record<string, string | string[] | number | number[] | undefined>;
}

const resolveQueryParams = ({ content, routeSlugPair, hrefPath, queries }: ResolveQueryParamsProps) => {
  return parseQueryParamsObject({
    ...(hrefPath.match(ID_REGEX) && content.id && typeof content.id === "number" && { id: content.id }),
    ...(hrefPath.match(SLUG_REGEX) && routeSlugPair?.slug && typeof routeSlugPair.slug === "string" && { slug: routeSlugPair.slug }),
    ...queries,
  });
};
